/* eslint-disable react/forbid-component-props */
import clsx from 'clsx'
import React from 'react'
import type { ReactNode } from 'react'
import {
  Dialog as ReactAriaDialog,
  Modal,
  ModalContext,
  ModalOverlay,
} from 'react-aria-components'
import { Backdrops } from '../Dialog/Dialog'
import Header from './Header'

export type DrawerPlacement = 'left' | 'right' | 'top' | 'bottom'

export interface DrawerProps {
  /**
   * A label that describes the drawer for screen readers.
   */
  ariaLabel?: string
  /**
   * The id of an element that labels the drawer for screen readers.
   */
  ariaLabelledBy?: string
  /**
   * The backdrop style for the drawer overlay: Dark=0 (dark overlay), Light=1 (white overlay)
   * @default Backdrops.Dark
   */
  backdrop?: Backdrops
  /**
   * The content to display inside the drawer panel
   */
  children: ReactNode
  /**
   * Whether the overlay behind the drawer panel is blurred
   * @default true
   */
  backdropBlurred?: boolean
  /**
   * Whether the drawer can be dismissed by clicking the overlay or pressing Escape
   * @default true
   */
  dismissable?: boolean
  /**
   * Whether the drawer is currently open
   */
  open?: boolean
  /**
   * Callback fired when the drawer open state changes
   */
  onOpenChange?: (open: boolean) => void
  /**
   * Offsets the drawer panel from one or more edges of the screen.
   * The overlay always remains full-screen regardless of this value.
   * @example { top: '64px' }
   */
  panelOffset?: { top?: string; right?: string; bottom?: string; left?: string }
  /**
   * The edge of the screen the drawer slides in from
   * @default 'right'
   */
  placement?: DrawerPlacement
}

function Drawer({
  ariaLabel,
  ariaLabelledBy,
  backdrop = Backdrops.Dark,
  backdropBlurred = true,
  children,
  dismissable = true,
  open = false,
  onOpenChange,
  panelOffset,
  placement = 'right',
}: DrawerProps): JSX.Element {
  const overlayStyles = clsx(
    'react-aria-drawer-overlay fixed inset-0 z-[9997]',
    backdrop === Backdrops.Light ? 'bg-white-45' : 'bg-black-45',
    backdropBlurred && 'backdrop-blur-2',
  )

  const drawerStyles =
    'react-aria-Drawer fixed bg-white-100 overflow-y-auto shadow-16 z-[9998]'

  const panelStyle: React.CSSProperties = panelOffset ?? {}

  return (
    <ModalContext.Provider value={{ isDismissable: dismissable, onOpenChange }}>
      <ModalOverlay
        className={overlayStyles}
        isKeyboardDismissDisabled={!dismissable}
        isOpen={open}
        // 👇 Needed so that interactions with elements inside the drawer, such as the Select
        // component, do not trigger the outside click handler that would close the drawer
        shouldCloseOnInteractOutside={(element) => {
          return !element.closest('[id^="coconut-portal-root-"]')
        }}
      >
        <Modal
          className={drawerStyles}
          data-placement={placement}
          style={panelStyle} // Use inline styles to have higher specificity than the default placement styles that set all offsets to 0
        >
          <ReactAriaDialog
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
          >
            {children}
          </ReactAriaDialog>
        </Modal>
      </ModalOverlay>
    </ModalContext.Provider>
  )
}

Drawer.Header = Header

export default Drawer
